import { createRouter, createWebHashHistory, createWebHistory, type RouteRecordName, type RouteRecordRaw } from "vue-router"
import { constantRoutes } from "./routes"
import business from "./routes/modules/business"
import dashboard from "./routes/modules/dashboard"
import globalRoutes from "./routes/modules/global"
import hq from "./routes/modules/hq"
import membership from "./routes/modules/membership"
import order from "./routes/modules/order"
import procurement from "./routes/modules/procurement"
import reservation from "./routes/modules/reservation"
import setting from "./routes/modules/setting"
import stock from "./routes/modules/stock"

const modulesRoutes: RouteRecordRaw[] = [
  ...business,
  ...order,
  ...reservation,
  ...dashboard,
  ...membership,
  ...hq,
  ...setting,
  ...procurement,
  ...stock,
  ...globalRoutes,
].sort((a, b) => {
  const orderA = (a.meta?.order as number) ?? 0
  const orderB = (b.meta?.order as number) ?? 0
  return orderA - orderB
})

const GENERATED_DYNAMIC_ROUTE_NAME_PREFIX = "__dynamic_route__"

const normalizeRoutePath = (path: string) => {
  if (!path || path === "/") return "/"
  return (path.startsWith("/") ? path : `/${path}`).replace(/\/+$/, "") || "/"
}

const resolveChildPath = (parentPath: string, routePath: string) => {
  if (!routePath) return normalizeRoutePath(parentPath)
  if (routePath.startsWith("/")) return normalizeRoutePath(routePath)
  return normalizeRoutePath(`${normalizeRoutePath(parentPath)}/${routePath}`)
}

const createGeneratedRouteName = (fullPath: string) => {
  const normalizedPath = fullPath.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "")
  return `${GENERATED_DYNAMIC_ROUTE_NAME_PREFIX}${normalizedPath || "root"}`
}

const createUniqueRouteName = (baseName: string, usedNames: Set<RouteRecordName>) => {
  let name = baseName
  let suffix = 1
  while (usedNames.has(name)) {
    name = `${baseName}_${suffix}`
    suffix += 1
  }
  usedNames.add(name)
  return name
}

const ensureDynamicRouteNames = (
  routes: RouteRecordRaw[],
  parentPath = "",
  usedNames = new Set<RouteRecordName>(),
) => {
  routes.forEach((route) => {
    const fullPath = resolveChildPath(parentPath, route.path)
    if (route.name) {
      usedNames.add(route.name)
    } else {
      route.name = createUniqueRouteName(createGeneratedRouteName(fullPath), usedNames)
    }
    if (route.children?.length) {
      ensureDynamicRouteNames(route.children, fullPath, usedNames)
    }
  })
}

ensureDynamicRouteNames(modulesRoutes)
/**
 * 动态路由
 * 用来放置有权限 (Roles 属性) 的路由
 * 必须带有 Name 属性
 */
export const asyncRoutes: RouteRecordRaw[] = [
  ...modulesRoutes,
  {
    path: "/:pathMatch(.*)*", // 必须将 'ErrorPage' 路由放在最后
    redirect: "/404",
    name: "ErrorPage",
    meta: {
      hidden: true,
    },
  },
]

const router = createRouter({
  history:
    import.meta.env.VITE_ROUTER_HISTORY === "hash"
      ? createWebHashHistory(import.meta.env.VITE_PUBLIC_PATH)
      : createWebHistory(import.meta.env.VITE_PUBLIC_PATH),
  routes: constantRoutes,
  scrollBehavior() {
    // // 始终滚动到顶部
    return { left: 0, top: 0 }
  },
})

type DynamicRouteName = NonNullable<RouteRecordName>

function collectRouteNames(routes: RouteRecordRaw[], names = new Set<DynamicRouteName>()) {
  routes.forEach((route) => {
    const routeName = route.name
    if (typeof routeName === "string" || typeof routeName === "symbol") {
      names.add(routeName)
    }
    if (route.children?.length) {
      collectRouteNames(route.children, names)
    }
  })
  return names
}

const asyncRouteNames = collectRouteNames(asyncRoutes)

/** 重置路由 */
export function resetRouter() {
  asyncRouteNames.forEach((name) => {
    if (router.hasRoute(name)) {
      router.removeRoute(name)
    }
  })
}

export default router
